import {useTranslation} from 'react-i18next';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Box, Button, CircularProgress, Grid, Typography, useMediaQuery} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionPaper} from '../EmpionPaper/EmpionPaper';

import {greenColors, orangeColors} from '../../layouts/theme/designSystem';

interface Section {
  isFinished: boolean;
  isLocked?: boolean;
}

interface Props {
  sections: Section[];
  currentPosition: number;
  onIndexChange: (index: number) => void;
  onComplete: () => void;
  onNext?: () => void;
  canComplete?: boolean;
  isLoadingMore?: boolean;
}

export default function QuestionNavigator({
  currentPosition,
  sections,
  onIndexChange,
  onComplete,
  onNext,
  canComplete = true,
  isLoadingMore,
}: Props) {
  const {t} = useTranslation();

  const isMobile = useMediaQuery('(max-width:769px)');
  const useCompactNavigator = isMobile;
  const isComplete = sections.every((s) => s.isFinished);
  const shouldDisplayCompleteButton = canComplete && currentPosition === sections.length - 1 && isComplete;

  const goToNextQuestion = () => {
    if (onNext) {
      onNext();
      return;
    }

    const nextIndex = currentPosition + 1;
    if (nextIndex < sections.length) {
      onIndexChange(nextIndex);
    }
  };

  const goToPreviousQuestion = () => {
    const previousIndex = currentPosition - 1;
    if (previousIndex >= 0) {
      onIndexChange(previousIndex);
    }
  };

  const goToQuestion = (index: number) => {
    if (index < 0 || index >= sections.length) {
      return;
    }

    const isCurrent = index === currentPosition;
    const isLocked = sections[index]?.isLocked;
    const isFinished = sections[index]?.isFinished;
    const isClickable = isCurrent || (!isLocked && isFinished);

    if (!isClickable) {
      return;
    }

    onIndexChange(index);
  };

  return (
    <>
      <Box textAlign={'center'} sx={{width: '100%'}}>
        <Grid container columns={12} sx={{width: '100%', maxWidth: '100%'}}>
          {!useCompactNavigator ? (
            <Grid item xs={12}>
              <EmpionPaper
                sx={{
                  maxWidth: 600,
                  margin: '0 auto',
                  borderRadius: 3,
                  border: `1px solid ${orangeColors[4]}`,
                  px: 1,
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                  }}
                >
                  <EmpionButton
                    onClick={goToPreviousQuestion}
                    disabled={currentPosition === 0}
                    sx={{minWidth: 40, flexShrink: 0}}
                  >
                    <ArrowBackIcon sx={{fontSize: 20}} />
                  </EmpionButton>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      flex: '1 1 0',
                      minWidth: 0,
                    }}
                  >
                    {sections.map((section, index) => {
                      const isCurrent = currentPosition === index;
                      const isFinished = section.isFinished;
                      const isLocked = section.isLocked;
                      const isStepClickable = isCurrent || (!isLocked && isFinished);

                      let backgroundColor = 'transparent';
                      let borderColor: string | undefined;
                      let color = 'inherit';
                      let label: React.ReactNode = index + 1;

                      if (isCurrent) {
                        backgroundColor = greenColors[700];
                        color = '#fff';
                        label = index + 1;
                      } else if (isFinished) {
                        borderColor = greenColors[100];
                        label = '✓';
                      } else if (isLocked) {
                        color = 'text.disabled';
                      }

                      const hoverBackgroundColor = isStepClickable
                        ? (isCurrent ? greenColors[100] : orangeColors[4])
                        : backgroundColor;

                      return (
                        <Button
                          key={index}
                          onClick={() => goToQuestion(index)}
                          disabled={!isStepClickable}
                          size='small'
                          sx={{
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            p: 0,
                            backgroundColor,
                            borderColor,
                            color,
                            flexShrink: 0,
                            borderRadius: '50%',
                            borderStyle: borderColor ? 'solid' : 'none',
                            borderWidth: borderColor ? 2 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isStepClickable ? 1 : 0.55,
                            cursor: isStepClickable ? 'pointer' : 'not-allowed',
                            '&:hover': {
                              backgroundColor: hoverBackgroundColor,
                            },
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Box>
                  {shouldDisplayCompleteButton ? (
                    <EmpionButton onClick={onComplete} size='small' sx={{flexShrink: 0}}>
                      {t('questionNavigator.buttons.complete')}
                    </EmpionButton>
                  ) : (
                    <EmpionButton
                      disabled={
                        isLoadingMore ||
                        !sections[currentPosition]?.isFinished ||
                        (!onNext && currentPosition === sections.length - 1)
                      }
                      onClick={goToNextQuestion}
                      sx={{minWidth: 40, flexShrink: 0}}
                    >
                      {isLoadingMore ? (
                        <CircularProgress size={20} sx={{color: 'inherit'}} />
                      ) : (
                        <ArrowForwardIcon sx={{fontSize: 20}} />
                      )}
                    </EmpionButton>
                  )}
                </Box>
              </EmpionPaper>
            </Grid>
          ) : (
            <>
              <Grid
                item
                xs={3}
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  justifyContent: 'right',
                  alignItems: 'center',
                  paddingTop: 1,
                  paddingBottom: 1,
                }}
              >
                <EmpionButton
                  onClick={goToPreviousQuestion}
                  disabled={currentPosition === 0}
                  sx={{minWidth: {xs: 32, sm: 40}}}
                >
                  <ArrowBackIcon sx={{fontSize: {xs: 20, sm: 24}}} />
                </EmpionButton>
              </Grid>
              <Grid item xs={6} display={'flex'} alignSelf={'center'} justifyContent={'center'} alignItems='center'>
                <Typography
                  variant='h4'
                  sx={{
                    fontSize: {xs: '1rem', sm: '1.25rem'},
                    fontWeight: 600,
                  }}
                >
                  {`${currentPosition + 1} ${t('shared.of')} ${sections.length}`}
                </Typography>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{
                  display: 'flex',
                  textAlign: 'center',
                  justifyContent: 'left',
                  alignItems: 'center',
                  paddingTop: 1,
                  paddingBottom: 1,
                }}
              >
                {shouldDisplayCompleteButton ? (
                  <EmpionButton onClick={onComplete} size='small'>
                    {t('questionNavigator.buttons.complete')}
                  </EmpionButton>
                ) : (
                  <EmpionButton
                    disabled={
                      isLoadingMore ||
                      !sections[currentPosition]?.isFinished ||
                      (!onNext && currentPosition === sections.length - 1)
                    }
                    onClick={goToNextQuestion}
                    sx={{minWidth: {xs: 32, sm: 40}}}
                  >
                    {isLoadingMore ? (
                      <CircularProgress size={16} sx={{color: 'inherit'}} />
                    ) : (
                      <ArrowForwardIcon sx={{fontSize: {xs: 20, sm: 24}}} />
                    )}
                  </EmpionButton>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </>
  );
}
